import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, SlideChip, SlideTitle, Card3D, Punchline, glow } from "./shared";

const TOTAL = 18;

const fadeOut = (frame: number, durationInFrames: number) =>
  interpolate(frame, [durationInFrames - 24, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

const spAppear = (frame: number, fps: number, delay = 0) =>
  interpolate(spring({ frame: frame - delay, fps, config: SPRING.snappy }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

/* ──────────────── Scene 07 — Enter EAKC ──────────────── */
export const S07Enter: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const titleSp = spring({ frame, fps, config: SPRING.bouncy });
  const titleScale = interpolate(titleSp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonGreen} />

      {/* Pulsing shield center */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: 520, height: 520,
        transform: `translate(-50%, -50%) scale(${titleScale * (1 + Math.sin(frame * 0.08) * 0.03)})`,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${COLORS.neonGreen}44, ${COLORS.neonGreen}11 60%, transparent)`,
        border: `4px solid ${COLORS.neonGreen}`,
        boxShadow: `0 0 100px ${COLORS.neonGreen}aa, inset 0 0 80px ${COLORS.neonGreen}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 300,
        filter: `drop-shadow(0 0 40px ${COLORS.neonGreen})`,
      }}>
        🛡️
      </div>

      <div style={{
        position: "absolute", bottom: 140, left: 0, right: 0,
        textAlign: "center",
        opacity: ap,
      }}>
        <div style={{
          fontSize: 68,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.textPrimary,
          textShadow: `0 0 40px ${COLORS.neonGreen}dd`,
          letterSpacing: -1,
        }}>
          Enter <span style={{ color: COLORS.neonGreen }}>EAKC.</span>
        </div>
        <div style={{
          fontSize: 28,
          fontFamily: FONTS.body,
          color: COLORS.textSecondary,
          marginTop: 14,
          fontWeight: 500,
          letterSpacing: 1,
        }}>
          7 security pillars · built in · not bolted on
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 08 — Pillar 1: Postgres RLS ──────────────── */
export const S08Pillar1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);
  const codeAp = spAppear(frame, fps, 40);

  const CODE_LINES = [
    { t: "CREATE POLICY tenant_isolation",    c: COLORS.neonPurple },
    { t: "  ON documents",                     c: COLORS.textPrimary },
    { t: "  USING (tenant_id = current_tenant())", c: COLORS.neonGreen },
    { t: "  WITH CHECK (tenant_id = current_tenant());", c: COLORS.neonGreen },
    { t: "",                                   c: COLORS.textPrimary },
    { t: "ALTER TABLE documents",              c: COLORS.neonPurple },
    { t: "  ENABLE ROW LEVEL SECURITY;",       c: COLORS.neonBlue },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonBlue} />
      <SlideChip n={8} total={TOTAL} color={COLORS.neonBlue} appear={ap} />
      <SlideTitle
        text="Pillar 1 · Postgres RLS"
        subtitle="Isolation enforced by the database — not the app."
        color={COLORS.neonBlue}
        appear={ap}
      />

      {/* Left: code card */}
      <div style={{
        position: "absolute", top: 310, left: 100,
        width: 900,
        opacity: codeAp,
        transform: `perspective(1400px) rotateY(4deg) rotateX(4deg) translateZ(0)`,
      }}>
        <div style={{
          background: COLORS.bgCardLight,
          border: `2.5px solid ${COLORS.neonBlue}`,
          borderRadius: 20,
          padding: "30px 38px",
          boxShadow: `0 0 40px ${COLORS.neonBlue}55, 0 20px 40px rgba(0,0,0,0.5)`,
        }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
            {[COLORS.neonRed, COLORS.neonYellow, COLORS.neonGreen].map((c, i) => (
              <div key={i} style={{ width: 12, height: 12, borderRadius: "50%", background: c, boxShadow: `0 0 8px ${c}` }} />
            ))}
            <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: COLORS.textMuted, marginLeft: 16 }}>migrations/001_tenant_rls.sql</div>
          </div>
          {CODE_LINES.map((line, i) => {
            const lineAp = spring({ frame: frame - 60 - i * 10, fps, config: SPRING.gentle });
            const lineOp = interpolate(lineAp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            return (
              <div key={i} style={{
                fontSize: 22,
                fontFamily: FONTS.mono,
                color: line.c,
                lineHeight: 1.7,
                opacity: lineOp,
                whiteSpace: "pre",
                textShadow: line.c !== COLORS.textPrimary ? glow(line.c) : "none",
              }}>
                {line.t || "\u00A0"}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: DB icon with shield */}
      <div style={{ position: "absolute", top: 380, right: 200 }}>
        <div style={{
          width: 360, height: 360,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.neonBlue}44, ${COLORS.neonBlue}11 60%, transparent)`,
          border: `4px solid ${COLORS.neonBlue}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 0 60px ${COLORS.neonBlue}aa, inset 0 0 50px ${COLORS.neonBlue}44`,
          fontSize: 200,
          transform: `scale(${1 + Math.sin(frame * 0.06) * 0.02})`,
        }}>
          🗄️
        </div>
      </div>

      <Punchline
        text={<>Even a bug in app code <span style={{ color: COLORS.neonBlue }}>cannot leak</span> data between tenants.</>}
        color={COLORS.textPrimary}
        appear={spAppear(frame, fps, 200)}
      />
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 09 — RLS Demo: Cross-Tenant Block ──────────────── */
export const S09RLSDemo: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  // Query chars typing
  const query = "SELECT * FROM documents WHERE tenant_id = 'globex';";
  const chars = Math.floor(interpolate(frame, [30, 130], [0, query.length], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  }));

  const blockAp = spAppear(frame, fps, 180);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonBlue} />
      <SlideChip n={9} total={TOTAL} color={COLORS.neonBlue} appear={ap} />
      <SlideTitle
        text="Demo · cross-tenant query denied"
        subtitle="Logged in as Acme. Asks for Globex data."
        color={COLORS.neonBlue}
        appear={ap}
      />

      {/* Query prompt */}
      <div style={{
        position: "absolute", top: 320, left: "50%", transform: "translateX(-50%)",
        width: 1500,
      }}>
        <div style={{
          background: COLORS.bgCardLight,
          border: `2.5px solid ${COLORS.neonCyan}`,
          borderRadius: 16,
          padding: "24px 36px",
          fontSize: 30,
          fontFamily: FONTS.mono,
          color: COLORS.textPrimary,
          boxShadow: `0 0 40px ${COLORS.neonCyan}55`,
        }}>
          <div style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 10, letterSpacing: 1.5 }}>
            SESSION: <span style={{ color: COLORS.neonBlue }}>tenant = 'acme'</span>
          </div>
          <span style={{ color: COLORS.neonCyan, fontWeight: 900, marginRight: 14 }}>psql&gt;</span>
          <span>{query.slice(0, chars)}</span>
          {chars < query.length && (
            <span style={{ display: "inline-block", width: 3, height: 30, background: COLORS.neonCyan, verticalAlign: "middle", marginLeft: 2, opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0 }} />
          )}
        </div>
      </div>

      {/* Result: BLOCKED with red X */}
      <div style={{
        position: "absolute", top: 500, left: "50%", transform: `translateX(-50%) scale(${blockAp})`,
        width: 1500,
        opacity: blockAp,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.neonRed}22, ${COLORS.bgCard}ee)`,
          border: `3px solid ${COLORS.neonRed}`,
          borderRadius: 20,
          padding: "36px 48px",
          boxShadow: `0 0 60px ${COLORS.neonRed}66`,
          display: "flex",
          alignItems: "center",
          gap: 36,
        }}>
          <div style={{ fontSize: 120, filter: `drop-shadow(0 0 30px ${COLORS.neonRed})` }}>🚫</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 38, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonRed, textShadow: glow(COLORS.neonRed, true), letterSpacing: -0.5 }}>
              (0 rows returned)
            </div>
            <div style={{ fontSize: 22, fontFamily: FONTS.mono, color: COLORS.textSecondary, marginTop: 12, lineHeight: 1.5 }}>
              RLS policy <span style={{ color: COLORS.neonCyan }}>tenant_isolation</span> filtered every row.
              <br />
              The app never saw Globex data. The DB refused to return it.
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 10 — Pillar 2: Self-Hosted Airgapped ──────────────── */
export const S10Pillar2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonGreen} />
      <SlideChip n={10} total={TOTAL} color={COLORS.neonGreen} appear={ap} />
      <SlideTitle
        text="Pillar 2 · Self-hosted · airgapped"
        subtitle="Your VPS. Your Postgres. Zero cloud dependencies."
        color={COLORS.neonGreen}
        appear={ap}
      />

      {/* Center: your VPS box */}
      <Card3D x={560} y={320} w={800} h={500} color={COLORS.neonGreen} frame={frame} delay={20} fps={fps}>
        <div style={{ textAlign: "center", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 28 }}>
          <div style={{ fontSize: 180 }}>🖥️</div>
          <div style={{ fontSize: 44, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonGreen, textShadow: glow(COLORS.neonGreen, true), letterSpacing: -0.5 }}>
            Your VPS
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap" }}>
            {["Postgres", "Qwen3 embeds", "EAKC API", "Your data"].map((t) => (
              <div key={t} style={{
                fontSize: 18,
                fontFamily: FONTS.mono,
                color: COLORS.neonGreen,
                padding: "8px 16px",
                borderRadius: 10,
                background: `${COLORS.neonGreen}15`,
                border: `1.5px solid ${COLORS.neonGreen}80`,
                textShadow: glow(COLORS.neonGreen),
              }}>✓ {t}</div>
            ))}
          </div>
        </div>
      </Card3D>

      {/* Crossed-out cloud providers on sides */}
      {[
        { icon: "☁️", label: "AWS Bedrock",  c: COLORS.neonRed, x: 120,  y: 440 },
        { icon: "☁️", label: "OpenAI API",   c: COLORS.neonRed, x: 120,  y: 620 },
        { icon: "☁️", label: "Pinecone",     c: COLORS.neonRed, x: 1520, y: 440 },
        { icon: "☁️", label: "Anthropic",    c: COLORS.neonRed, x: 1520, y: 620 },
      ].map((c, i) => {
        const sp = spring({ frame: frame - 50 - i * 12, fps, config: SPRING.gentle });
        const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        return (
          <div key={i} style={{
            position: "absolute", left: c.x, top: c.y,
            width: 280, height: 130,
            opacity: op,
          }}>
            <div style={{
              width: "100%", height: "100%",
              background: `linear-gradient(135deg, ${c.c}11, ${COLORS.bgCard}ee)`,
              border: `2px dashed ${c.c}`,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "0 20px",
              position: "relative",
              filter: "grayscale(0.4)",
            }}>
              <div style={{ fontSize: 54, opacity: 0.5 }}>{c.icon}</div>
              <div style={{ fontSize: 22, fontFamily: FONTS.heading, fontWeight: 700, color: c.c, textDecoration: "line-through", textDecorationColor: c.c, textDecorationThickness: 3 }}>
                {c.label}
              </div>
            </div>
          </div>
        );
      })}

      <Punchline
        text={<>Run it <span style={{ color: COLORS.neonGreen }}>fully airgapped</span>. No external calls. Ever.</>}
        color={COLORS.textPrimary}
        appear={spAppear(frame, fps, 160)}
      />
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 11 — Pillar 3: Open Embeddings ──────────────── */
export const S11Pillar3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonPurple} />
      <SlideChip n={11} total={TOTAL} color={COLORS.neonPurple} appear={ap} />
      <SlideTitle
        text="Pillar 3 · Open-source embeddings"
        subtitle="Qwen3 runs locally. Zero API calls to OpenAI."
        color={COLORS.neonPurple}
        appear={ap}
      />

      {/* Split comparison: OpenAI (crossed out) vs Qwen3 (live) */}
      <div style={{
        position: "absolute", top: 320, left: 0, right: 0,
        display: "flex",
        justifyContent: "center",
        gap: 100,
      }}>
        {[
          {
            name: "OpenAI text-embedding-3",
            items: ["$0.13 per 1M tokens", "Every call hits OpenAI", "Your query = their log", "SLA at their mercy"],
            c: COLORS.neonRed,
            crossed: true,
            icon: "☁️",
          },
          {
            name: "Qwen3 8B embeddings",
            items: ["Free · open weights", "Runs on your GPU", "Zero outbound traffic", "Deterministic forever"],
            c: COLORS.neonGreen,
            crossed: false,
            icon: "🧠",
          },
        ].map((col, i) => (
          <Card3D key={col.name} inline w={620} h={520} color={col.c} frame={frame} delay={30 + i * 20} fps={fps} rotY={(i === 0 ? 4 : -4)}>
            <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 22 }}>
              <div style={{ fontSize: 80, textAlign: "center", opacity: col.crossed ? 0.5 : 1, filter: col.crossed ? "grayscale(0.6)" : "none" }}>
                {col.icon}
              </div>
              <div style={{
                fontSize: 30,
                fontFamily: FONTS.heading,
                fontWeight: 900,
                color: col.c,
                textAlign: "center",
                textShadow: glow(col.c, true),
                textDecoration: col.crossed ? "line-through" : "none",
                textDecorationThickness: 3,
                letterSpacing: -0.5,
              }}>
                {col.name}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
                {col.items.map((x, j) => (
                  <div key={j} style={{
                    fontSize: 20,
                    fontFamily: FONTS.body,
                    color: COLORS.textSecondary,
                    paddingLeft: 20,
                    borderLeft: `4px solid ${col.c}`,
                    opacity: col.crossed ? 0.6 : 1,
                  }}>{x}</div>
                ))}
              </div>
            </div>
          </Card3D>
        ))}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 12 — Pillar 4: Web Nudge ──────────────── */
export const S12Pillar4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonCyan} />
      <SlideChip n={12} total={TOTAL} color={COLORS.neonCyan} appear={ap} />
      <SlideTitle
        text="Pillar 4 · Opt-in web search"
        subtitle="Every outbound hop is a user decision — not a default."
        color={COLORS.neonCyan}
        appear={ap}
      />

      {/* UI mockup: nudge dialog */}
      <div style={{
        position: "absolute", top: 300, left: "50%",
        transform: `translateX(-50%) perspective(1600px) rotateX(4deg)`,
        width: 1500,
        opacity: spAppear(frame, fps, 20),
      }}>
        <div style={{
          background: COLORS.bgCardLight,
          border: `3px solid ${COLORS.neonCyan}`,
          borderRadius: 22,
          padding: "36px 48px",
          boxShadow: `0 0 60px ${COLORS.neonCyan}66, 0 30px 60px rgba(0,0,0,0.6)`,
        }}>
          <div style={{
            fontSize: 22,
            fontFamily: FONTS.mono,
            color: COLORS.neonCyan,
            letterSpacing: 2,
            textTransform: "uppercase",
            marginBottom: 20,
            textShadow: glow(COLORS.neonCyan),
          }}>
            🔍 Web search nudge — 2 sub-queries need external data
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {[
              { q: "Pricing for Acme enterprise plan",         found: true,  src: "contracts/acme-v3.pdf" },
              { q: "Latest CVE in our dependency tree",         found: false, web: "brave search · npm audit" },
              { q: "Internal ADR on database choice",           found: true,  src: "adr-0042-postgres.md" },
              { q: "Competitor pricing for similar feature",    found: false, web: "brave search · perplexity" },
            ].map((row, i) => {
              const sp = spring({ frame: frame - 60 - i * 15, fps, config: SPRING.bouncy });
              const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const tx = interpolate(sp, [0, 1], [-60, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              return (
                <div key={i} style={{
                  opacity: op,
                  transform: `translateX(${tx}px)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  padding: "16px 24px",
                  borderRadius: 12,
                  background: row.found ? `${COLORS.neonGreen}10` : `${COLORS.neonOrange}10`,
                  border: `1.5px solid ${row.found ? COLORS.neonGreen : COLORS.neonOrange}88`,
                }}>
                  <div style={{
                    width: 32, height: 32,
                    borderRadius: 6,
                    border: `2px solid ${row.found ? COLORS.neonGreen : COLORS.neonOrange}`,
                    background: row.found ? `${COLORS.neonGreen}22` : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    color: row.found ? COLORS.neonGreen : COLORS.neonOrange,
                    fontWeight: 900,
                  }}>
                    {row.found ? "✓" : "☐"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontFamily: FONTS.body, color: COLORS.textPrimary, fontWeight: 600 }}>
                      {row.q}
                    </div>
                    <div style={{ fontSize: 15, fontFamily: FONTS.mono, color: row.found ? COLORS.neonGreen : COLORS.neonOrange, marginTop: 4 }}>
                      {row.found ? `found in ${row.src}` : `web search: ${row.web}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "flex-end",
            gap: 14,
          }}>
            <div style={{
              padding: "12px 26px",
              borderRadius: 12,
              background: COLORS.bgCard,
              border: `1.5px solid ${COLORS.textMuted}88`,
              fontSize: 18,
              fontFamily: FONTS.body,
              color: COLORS.textSecondary,
              fontWeight: 600,
            }}>Cancel</div>
            <div style={{
              padding: "12px 26px",
              borderRadius: 12,
              background: `${COLORS.neonCyan}22`,
              border: `2px solid ${COLORS.neonCyan}`,
              fontSize: 18,
              fontFamily: FONTS.body,
              color: COLORS.neonCyan,
              fontWeight: 700,
              textShadow: glow(COLORS.neonCyan),
            }}>Run web search for 2 items</div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
